/**
 * 1.3.75: MVP 多租户架构 - 组织设置页面
 * 组织信息编辑、升级等功能
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  getCurrentOrganization,
  getOrganizationDetails,
  updateOrganization,
  upgradeToBusiness,
  type UserOrganization,
  type OrganizationDetails,
} from '../services/organizationService';
import { PLAN_CONFIGS } from '../types/organization';

export function OrganizationSettingsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  
  const [organization, setOrganization] = useState<UserOrganization | null>(null);
  const [details, setDetails] = useState<OrganizationDetails | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  
  const [formData, setFormData] = useState({
    name: '',
  });

  useEffect(() => {
    loadOrganization();
  }, []);

  const loadOrganization = async () => {
    setIsLoading(true);
    try {
      const { organization: org } = await getCurrentOrganization();
      if (org) {
        setOrganization(org);
        setFormData({ name: org.name });
        
        const { details: orgDetails } = await getOrganizationDetails(org.id);
        setDetails(orgDetails);
      }
    } catch (err: any) {
      setError(err.message || '加载失败');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async () => {
    if (!organization) return;
    
    setIsSaving(true);
    setError(null);
    setSuccess(null);
    
    try {
      const { organization: updated, error: apiError } = await updateOrganization(
        organization.id,
        { name: formData.name }
      );
      
      if (apiError) {
        setError(apiError.message);
        return;
      }
      
      setSuccess('保存成功');
      if (updated) {
        setOrganization(prev => prev ? { ...prev, name: updated.name } : null);
      }
    } catch (err: any) {
      setError(err.message || '保存失败');
    } finally {
      setIsSaving(false);
    }
  };

  const planConfig = organization ? PLAN_CONFIGS[organization.plan_type] : null;
  const isPersonal = details?.organization?.org_type === 'personal';
  const isOwner = organization?.role === 'owner';

  if (isLoading) {
    return (
      <div className="p-8 flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          {/* 1.3.90: 页面loading只显示YUI动画 */}
          <span className="inline-block text-primary font-semibold yui-loading-animation text-2xl">YUI</span>
        </div>
      </div>
    );
  }

  if (!organization) {
    return (
      <div className="p-8">
        <div className="text-center text-gray-500">
          未找到组织信息
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-4xl mx-auto">
      {/* 页面标题 */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">组织设置</h1>
        <p className="text-gray-500 mt-1">管理您的组织信息和配置</p>
      </div>

      {/* 提示信息 */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl text-red-600">
          {error}
        </div>
      )}
      {success && (
        <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-xl text-green-600">
          {success}
        </div>
      )}

      {/* 基本信息卡片 */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 mb-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">基本信息</h2>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              组织名称
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
              disabled={!isOwner}
              className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none transition-all disabled:bg-gray-50 disabled:text-gray-500"
            />
          </div>

          <div className="flex items-center gap-4">
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                组织类型
              </label>
              <div className="px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-gray-700">
                {isPersonal ? '👤 个人' : '🏢 企业'}
              </div>
            </div>
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                当前套餐
              </label>
              <div className="px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-gray-700">
                {planConfig?.name || organization.plan_type}
              </div>
            </div>
          </div>

          {isOwner && (
            <div className="pt-4">
              <button
                onClick={handleSave}
                disabled={isSaving}
                className="px-6 py-2 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 transition-colors disabled:opacity-50"
              >
                {isSaving ? '保存中...' : '保存更改'}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* 配额信息卡片 */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 mb-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">使用配额</h2>
        
        <div className="grid md:grid-cols-3 gap-4">
          <div className="bg-gray-50 rounded-xl p-4">
            <div className="text-sm text-gray-500 mb-1">成员数量</div>
            <div className="text-2xl font-bold text-gray-900">
              {organization.current_members} / {organization.max_members}
            </div>
          </div>
          <div className="bg-gray-50 rounded-xl p-4">
            <div className="text-sm text-gray-500 mb-1">存储空间</div>
            <div className="text-2xl font-bold text-gray-900">
              {details?.organization?.max_storage_gb || 1} GB
            </div>
          </div>
          <div className="bg-gray-50 rounded-xl p-4">
            <div className="text-sm text-gray-500 mb-1">公开链接配额</div>
            <div className="text-2xl font-bold text-gray-900">
              {details?.organization?.public_link_quota?.toLocaleString() || 0} 次/月
            </div>
          </div>
        </div>
      </div>

      {/* 升级提示（仅个人用户显示） */}
      {isPersonal && isOwner && (
        <div className="bg-gradient-to-r from-purple-500 to-pink-500 rounded-2xl p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold mb-2">升级为企业账户</h3>
              <p className="text-white/80 text-sm">
                解锁团队协作、共享知识库等更多功能
              </p>
            </div>
            <button
              onClick={() => setShowUpgradeModal(true)}
              className="px-6 py-2 bg-white text-purple-600 font-medium rounded-xl hover:bg-white/90 transition-colors"
            >
              立即升级
            </button>
          </div>
        </div>
      )}

      {/* 成员管理入口 */}
      {!isPersonal && (
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 mt-6">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">成员管理</h2>
              <p className="text-gray-500 text-sm mt-1">
                管理组织成员、邀请新成员
              </p>
            </div>
            <button
              onClick={() => navigate('/settings/members')}
              className="px-4 py-2 text-indigo-600 hover:bg-indigo-50 rounded-xl transition-colors"
            >
              前往管理 →
            </button>
          </div>
        </div>
      )}

      {/* 升级弹窗 */}
      {showUpgradeModal && (
        <UpgradeModal
          onClose={() => setShowUpgradeModal(false)}
          onSuccess={() => {
            setShowUpgradeModal(false);
            loadOrganization();
          }}
        />
      )}
    </div>
  );
}

// 升级弹窗组件
function UpgradeModal({ onClose, onSuccess }: { onClose: () => void; onSuccess: () => void }) {
  const [formData, setFormData] = useState({
    companyName: '',
    companySize: '',
    industry: '',
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const COMPANY_SIZES = [
    { value: '1-5', label: '1-5 人' },
    { value: '6-20', label: '6-20 人' },
    { value: '21-50', label: '21-50 人' },
    { value: '51+', label: '51+ 人' },
  ];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.companyName.trim()) {
      setError('请输入企业名称');
      return;
    }
    if (!formData.companySize) {
      setError('请选择企业规模');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const { result, error: apiError } = await upgradeToBusiness(
        formData.companyName.trim(),
        formData.companySize,
        formData.industry || undefined
      );

      if (apiError) {
        setError(apiError.message);
        return;
      }

      onSuccess();
    } catch (err: any) {
      setError(err.message || '升级失败');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl p-6 max-w-md w-full">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-gray-900">升级为企业账户</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              企业名称 <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.companyName}
              onChange={(e) => setFormData(prev => ({ ...prev, companyName: e.target.value }))}
              className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              企业规模 <span className="text-red-500">*</span>
            </label>
            <div className="grid grid-cols-2 gap-2">
              {COMPANY_SIZES.map(size => (
                <label
                  key={size.value}
                  className={`flex items-center justify-center px-3 py-2 border rounded-lg cursor-pointer text-sm transition-all
                    ${formData.companySize === size.value 
                      ? 'border-purple-500 bg-purple-50 text-purple-700' 
                      : 'border-gray-200 hover:border-purple-300'}`}
                >
                  <input
                    type="radio"
                    name="companySize"
                    value={size.value}
                    checked={formData.companySize === size.value}
                    onChange={(e) => setFormData(prev => ({ ...prev, companySize: e.target.value }))}
                    className="sr-only"
                  />
                  {size.label}
                </label>
              ))}
            </div>
          </div>

          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">
              {error}
            </div>
          )}

          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-200 text-gray-700 rounded-xl hover:bg-gray-50 transition-colors"
            >
              取消
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="flex-1 px-4 py-2 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-xl hover:from-purple-600 hover:to-pink-600 transition-colors disabled:opacity-50"
            >
              {isLoading ? '升级中...' : '确认升级'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
